import * as React from 'react';

import { Input } from './input';

function formatDateString(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

function parseDateString(value: string): Date | undefined {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;

  const month = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  if (month < 1 || month > 12) return undefined;
  if (day < 1 || day > 31) return undefined;
  if (year < 1900 || year > new Date().getFullYear()) return undefined;

  const date = new Date(year, month - 1, day);

  if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
    return undefined;
  }

  return date;
}

function dateToDisplayString(date: Date | undefined): string {
  if (!date || isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${month}/${day}/${year}`;
}

function datesEqual(a: Date | undefined, b: Date | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.getTime() === b.getTime();
}

interface DateInputProps extends Omit<
  React.ComponentProps<'input'>,
  'value' | 'onChange' | 'type'
> {
  value?: Date;
  /** Controls the formatted text when a parent needs the unfinished input to survive remounts. */
  inputValue?: string;
  onChange?: (date: Date | undefined) => void;
  onInputValueChange?: (value: string) => void;
  /** Reports whether the current typed value is empty or a complete, valid calendar date. */
  onInputValidityChange?: (isValid: boolean) => void;
}

function DateInput({
  value,
  inputValue,
  onChange,
  onInputValueChange,
  onInputValidityChange,
  ...props
}: DateInputProps) {
  const [displayValue, setDisplayValue] = React.useState(() => dateToDisplayString(value));
  const lastEmittedRef = React.useRef<Date | undefined>(value);

  const updateDisplayValue = React.useCallback(
    (nextValue: string) => {
      setDisplayValue(nextValue);
      onInputValueChange?.(nextValue);
    },
    [onInputValueChange],
  );

  React.useEffect(() => {
    if (!datesEqual(value, lastEmittedRef.current)) {
      updateDisplayValue(dateToDisplayString(value));
      lastEmittedRef.current = value;
    }
  }, [updateDisplayValue, value]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatDateString(e.target.value);
    updateDisplayValue(formatted);

    const parsed = parseDateString(formatted);
    lastEmittedRef.current = parsed;
    onChange?.(parsed);
    onInputValidityChange?.(formatted.length === 0 || parsed !== undefined);
  }

  return (
    <Input
      {...props}
      type="text"
      inputMode="numeric"
      placeholder={props.placeholder ?? 'MM/DD/YYYY'}
      value={inputValue ?? displayValue}
      onChange={handleChange}
      maxLength={10}
    />
  );
}

export { DateInput };
export type { DateInputProps };
